import {
  CacheConfig,
  CacheStatus,
  getCachedItem,
  setCacheItem,
  setCacheStatus,
} from '@r-and-a-shared-ui/core-caching';
import { IContentItem } from '@kontent-ai/delivery-sdk';

import { deliveryClient } from '../config/kontent-delivery-client';
import { adjustSystemProperties } from '../utils';
import { hasValue } from '@r-and-a-shared-ui/utils';

type Input = {
  input: {
    slug: string;
    types?: string[];
    collection?: string;
    cache?: boolean;
    cacheTTL?: number;
  };
  cacheStatus?: CacheStatus;
};

export const getPageCodeNameBySlugInTypes = async <Type extends IContentItem>({
  input: {
    slug,
    types = ['page'],
    collection,
    cache = true,
    cacheTTL = CacheConfig.TTL.Page.valueOf() - 1,
  },
  cacheStatus,
}: Input): Promise<Type | undefined> => {
  const cacheKey = `getPageCodeNameBySlugInTypes-${types.join(
    ',',
  )}-${slug}-${collection}`;

  try {
    const cachedItem = getCachedItem<Type>(cacheKey, cache);

    if (cachedItem) {
      setCacheStatus(cacheStatus, {
        cacheable: cache,
        xInMemoryCache: CacheConfig.Hit,
        cacheTTL,
      });
      return cachedItem;
    }

    const fetchItem = async (
      filterField: string,
    ): Promise<Type | undefined> => {
      let query = deliveryClient.items<Type>().types(types).depthParameter(0);

      if (hasValue(collection)) {
        query = query.collection(collection as string);
      }

      const response = await query
        .equalsFilter(filterField, slug)
        .toPromise()
        .then(r => r.data.items[0]);

      adjustSystemProperties(response);
      if (response) {
        return response as Type;
      }

      return undefined;
    };

    let result = await fetchItem('elements.url_slug');

    if (!result) {
      result = await fetchItem('elements.special_url');
    }

    if (result) {
      setCacheItem<Type>(cacheKey, result, cacheTTL, cache);
    }

    setCacheStatus(cacheStatus, {
      cacheable: cache,
      xInMemoryCache: CacheConfig.Miss,
      cacheTTL,
    });

    return result;
  } catch (error: any) {
    console.error('Error in getPageCodeNameBySlug', error);
    throw error;
  }
};
